import { useEffect } from 'react';

import { queryOptions, useQuery } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';

import { QueryState, SortingState } from '@cast/design-system';
import { NotificationsListResponse } from '@cast/types';
import { useCompoundReducer } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

import { NotificationsFilter } from './useGetNotificationsQuery';

const NOTIFICATIONS_QUERY_KEY = 'notifications';

const getNotificationsPaginatedQueryKey = ({
  currentPage,
  filters,
  sortingState,
}: {
  currentPage: number;
  filters: NotificationsFilter;
  sortingState?: SortingState;
}) => [NOTIFICATIONS_QUERY_KEY, currentPage, filters, sortingState] as const;

const getNotificationsPaginatedQueryOptions = ({
  currentPage,
  filters,
  sortingState,
  currentCursor,
  pageSize,
  enabled,
  onDataLoaded,
}: {
  currentPage: number;
  filters: NotificationsFilter;
  sortingState?: SortingState;
  currentCursor?: string;
  pageSize: number;
  enabled: boolean;
  onDataLoaded: (data: NotificationsListResponse) => void;
}) =>
  queryOptions({
    queryKey: getNotificationsPaginatedQueryKey({
      currentPage,
      filters,
      sortingState,
    }),
    queryFn: async () => {
      const { data } = await apiClient.notifications.listNotifications({
        pageLimit: `${pageSize}`,
        pageCursor: currentCursor,
        filterClusterId: filters.clusterId,
        filterClusterName: filters.clusterName,
        filterNotificationId: filters.id,
        filterNotificationName: filters.name,
        filterProject: filters.project,
        filterOperationId: filters.operationId,
        filterIsAcked:
          typeof filters.onlyUnresolved === 'boolean'
            ? !filters.onlyUnresolved
            : undefined,
        filterSeverities: filters.severities,
        filterIsExpired: filters.isExpired,
        filterOperationType: filters.operationType,
        sortField: sortingState?.columnId,
        sortOrder: sortingState?.sortBy.direction || 'desc',
      });
      onDataLoaded(data);
      return data;
    },
    enabled,
    refetchInterval: currentPage === 0 ? 20_000 : false,
    gcTime: 0,
  });

type UseGetNotificationsPaginatedQueryParams = {
  filter: NotificationsFilter;
  sortingState?: SortingState;
  enabled?: boolean;
  pageSize?: number;
};

const initialState: QueryState<NotificationsFilter> = {
  currentPage: 0,
  filters: {},
  enabled: false,
};

export const useGetNotificationsPaginatedQuery = ({
  filter = {},
  sortingState,
  enabled = true,
  pageSize = 50,
}: UseGetNotificationsPaginatedQueryParams) => {
  const { dispatcher, state } = useCompoundReducer(
    {
      setState: (
        state,
        action: {
          filter: NotificationsFilter;
          sortingState?: SortingState;
          enabled: boolean;
        }
      ) => {
        if (
          action.enabled !== state.enabled ||
          !isEqual(action.filter, state.filters) ||
          !isEqual(action.sortingState, state.sortingState)
        ) {
          return {
            ...initialState,
            filters: action.filter,
            sortingState: action.sortingState,
            enabled: action.enabled,
          };
        }
        return state;
      },
      dataLoaded: (state, data: NotificationsListResponse) => {
        const previous = data.previousCursor;
        const next = data.nextCursor;
        return { ...state, cursors: { previous, next } };
      },
      fetchNextPage: (state) => {
        return {
          ...state,
          currentPage: state.currentPage + 1,
          currentCursor: state.cursors?.next,
        };
      },
      fetchPreviousPage: (state) => {
        return {
          ...state,
          currentPage: state.currentPage - 1,
          currentCursor: state.cursors?.previous,
        };
      },
    },
    initialState
  );

  const filterKey = JSON.stringify(filter);
  const sortingKey = JSON.stringify(sortingState);
  useEffect(() => {
    dispatcher.setState({ filter, sortingState, enabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, sortingKey, enabled]);

  const { data, isLoading, error, isFetching } = useQuery(
    getNotificationsPaginatedQueryOptions({
      currentPage: state.currentPage,
      filters: state.filters,
      sortingState: state.sortingState,
      currentCursor: state.currentCursor,
      pageSize,
      enabled,
      onDataLoaded: dispatcher.dataLoaded,
    })
  );

  return {
    fetchNextPage: () => {
      dispatcher.fetchNextPage();
    },
    fetchPreviousPage: () => {
      dispatcher.fetchPreviousPage();
    },
    isFetching,
    data,
    currentPage: state.currentPage,
    isLoading,
    error,
  };
};
